import React from "react";
import { Divider, makeStyles, tokens } from "@fluentui/react-components";
import { OutlineGroup, OutlineGroupProps } from "./OutlineGroup";
import "./OutlineList.css";

import type { TOutlineGroup } from "./types";

export type Division = {
  boundaryIndices?: [number, number];
  part?: { number?: string };
  chapter?: { number?: string; title?: string };
} | null;

export function getDivisionTitle(group?: TOutlineGroup | Division): string | undefined {
  if (group?.part && group.part.number) {
    return group.part.number;
  }

  if (group?.chapter) {
    const { number, title } = group.chapter;
    return number && title ? `${number} - ${title}` : `${number ?? title}`;
  }

  return undefined;
}

const useStyles = makeStyles({
  divider: {
    color: tokens.colorNeutralStroke2,
  },
});

export interface OutlineListProps extends Omit<
  OutlineGroupProps,
  "group" | "selectedGroup" | "selectedStyle"
> {
  groups: TOutlineGroup[];
  selectedBoundaryIndices: [number, number] | null;
  selectedGroupFilter: string | null;
  selectedStyleFilter: string | null;
}

export function OutlineList(props: OutlineListProps) {
  const styles = useStyles();

  // Chapter titles repeat — a book can hold two chapters both named
  // "Introduction" — so a chapter row is identified by the paragraph range it
  // covers, which is also what drives filtering and navigation.  Style-mode
  // groups carry no range and fall back to their (unique) category name.
  const isSelectedGroup = (group: TOutlineGroup) => {
    const selected = props.selectedBoundaryIndices;
    if (group.boundaryIndices && selected) {
      return group.boundaryIndices[0] === selected[0] && group.boundaryIndices[1] === selected[1];
    }
    return group.name === props.selectedGroupFilter;
  };

  return (
    <div className="outline-list">
      {props.groups.map((g, i) => {
        const chapterTitle = getDivisionTitle({ chapter: g.chapter });
        const partTitle = getDivisionTitle({ part: g.part });

        return (
          <React.Fragment key={`${g.id}-${i}`}>
            <OutlineGroup
              expandAll={props.expandAll}
              group={{ ...g, name: chapterTitle ?? partTitle ?? g.name }}
              ignoreParagraphs={props.ignoreParagraphs}
              outlineMode={props.outlineMode}
              selectedGroup={isSelectedGroup}
              selectedStyle={(style) => style === props.selectedStyleFilter}
              styleDefinitionMap={props.styleDefinitionMap}
              onClearGroup={props.onClearGroup}
              onClearStyle={props.onClearStyle}
              onSelectGroup={props.onSelectGroup}
              onSelectStyle={props.onSelectStyle}
              onSetBoundaryIndices={props.onSetBoundaryIndices}
            />
            {i < props.groups.length - 1 && <Divider className={styles.divider} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}
